import { useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useMarquees } from '../../hooks/useMarquees';
import styles from './MobileHeader.module.css';
import MarqueeBanner from '../common/MarqueeBanner';

export interface MobileHeaderProps {
  title?: string;
  showBack?: boolean;
  showSearch?: boolean;
  showMenu?: boolean;
  showMarquee?: boolean;
  transparent?: boolean;
  semiTransparent?: boolean;
  onBack?: () => void;
  onSearch?: () => void;
  onMenu?: () => void;
  rightAction?: ReactNode;
}

export const MobileHeader: React.FC<MobileHeaderProps> = ({
  title,
  showBack = true,
  showSearch = false,
  showMenu = false,
  showMarquee = false,
  transparent = false,
  semiTransparent = false,
  onBack,
  onSearch,
  onMenu,
  rightAction,
}) => {
  const navigate = useNavigate();
  const { marquees, enabled: marqueeEnabled, loading: marqueeLoading, settings: marqueeSettings } = useMarquees();

  // Show marquee only when prop AND global setting are both true
  const shouldShowMarquee = showMarquee && marqueeEnabled && !marqueeLoading;

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  const handleSearch = () => {
    if (onSearch) {
      onSearch();
    } else {
      // Default search behavior
      navigate('/search');
    }
  };

  const handleMenu = () => {
    if (onMenu) {
      onMenu();
    } else {
      // Default menu behavior
      // Could open a drawer or show options
      console.log('Menu clicked');
    }
  };

  return (
    <>
      <header
        className={`${styles.mobileHeader} ${
          transparent ? styles['mobileHeader--transparent'] : ''
        } ${
          semiTransparent ? styles['mobileHeader--semiTransparent'] : ''
        }`}
      >
        <div className={styles.mobileHeader__container}>
          {/* Left side - Back button */}
          {showBack && (
            <button
              className={styles.mobileHeader__button}
              onClick={handleBack}
              aria-label="返回"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Center - Title */}
          {title && (
            <h1 className={styles.mobileHeader__title}>{title}</h1>
          )}

          {/* Right side - Search and Menu */}
          <div className={styles.mobileHeader__actions}>
            {showSearch && (
              <button
                className={styles.mobileHeader__button}
                onClick={handleSearch}
                aria-label="搜尋"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="9" cy="9" r="7" />
                  <path d="M14 14l4 4" />
                </svg>
              </button>
            )}
            {showMenu && (
              <button
                className={styles.mobileHeader__button}
                onClick={handleMenu}
                aria-label="選單"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <circle cx="3" cy="3" r="1.5" />
                  <circle cx="10" cy="3" r="1.5" />
                  <circle cx="17" cy="3" r="1.5" />
                  <circle cx="3" cy="10" r="1.5" />
                  <circle cx="10" cy="10" r="1.5" />
                  <circle cx="17" cy="10" r="1.5" />
                  <circle cx="3" cy="17" r="1.5" />
                  <circle cx="10" cy="17" r="1.5" />
                  <circle cx="17" cy="17" r="1.5" />
                </svg>
              </button>
            )}
            {rightAction && <div className={styles.mobileHeader__rightAction}>{rightAction}</div>}
          </div>
        </div>
      </header>

      {/* Marquee Banner - rendered BELOW header when both prop and global setting are enabled */}
      {shouldShowMarquee && (
        <MarqueeBanner
          marquees={marquees}
          enabled={true}
          settings={marqueeSettings}
          position="below-header"
        />
      )}
    </>
  );
};

export default MobileHeader;
